import * as PIXI from 'pixi.js'
import { BlurFilter } from '@pixi/filter-blur'

const POSTIT_WIDTH = 360
const POSTIT_HEIGHT = 360
const PADDING = 24
const HEADING_FONT = 'fatfrank, Arial, Helvetica, sans-serif'
const MESSAGE_FONT = 'permanent-marker, Arial, Helvetica, sans-serif'


const SHADOW_BLUR = 12
const SHADOW_PAD = SHADOW_BLUR * 2
const SHADOW_OFFSET_X = 1
const SHADOW_OFFSET_Y = 4

let shadowTexture = null
let fill;

function getShadowTexture(renderer) {
    if (shadowTexture) return shadowTexture

    const g = new PIXI.Graphics()
    g.beginFill(0x000000, 0.15)
    g.drawRect(SHADOW_PAD, SHADOW_PAD, POSTIT_WIDTH, POSTIT_HEIGHT)
    g.endFill()

    const rt = PIXI.RenderTexture.create({
        width: POSTIT_WIDTH + SHADOW_PAD * 2,
        height: POSTIT_HEIGHT + SHADOW_PAD * 2,
    })
    g.filters = [new BlurFilter(SHADOW_BLUR, 3)]
    renderer.render(g, { renderTexture: rt })
    g.destroy(true)

    shadowTexture = rt
    return shadowTexture
}

export class PostIt extends PIXI.Container {
    constructor({ id, text, author_name, number, image_url, x, y, rotation }, assets) {
        super()

        this.targetX = x
        this.targetY = y
        this.targetRotation = (rotation * Math.PI) / 180
        this.sprite = 1;

        const hasImage = !!image_url
        let imgCornerX = 0, imgCornerY = 0

        // Drop shadow (shared texture, no per-element filter)
        if (assets.shadowTexture) {
            const shadow = new PIXI.Sprite(assets.shadowTexture)
            shadow.x = -SHADOW_PAD + SHADOW_OFFSET_X
            shadow.y = -SHADOW_PAD + SHADOW_OFFSET_Y
            this.addChild(shadow)
        }

        // Optional image — rendered BEFORE background so it sits behind the post-it
        if (hasImage) {
            const imgTexture = PIXI.Texture.from(image_url)
            const imgSprite = new PIXI.Sprite(imgTexture)
            const baseSize = POSTIT_WIDTH - PADDING * 2
            const imgSize = baseSize * 1.4
            const imgHeight = imgSize * (211.12 / 285.62) // natural aspect ratio
            const imgX = (POSTIT_WIDTH - imgSize) / 2     // centered horizontally
            const imgY = -imgHeight * 0.65                 // 15% overlaps the post-it top
            imgCornerX = imgX
            imgCornerY = imgY
            imgSprite.x = imgX
            imgSprite.y = imgY
            imgSprite.width = imgSize
            imgSprite.height = imgHeight

            // Frayed-edge mask
            const mask = new PIXI.Graphics()
            const sx = imgSize / 285.62
            const sy = imgHeight / 211.12
            mask.beginFill(0xffffff)
            mask.moveTo(285.14*sx, 128.93*sy)
            mask.bezierCurveTo(288.0*sx,116.05*sy, 277.31*sx,103.18*sy, 277.42*sx,90.23*sy)
            mask.bezierCurveTo(278.34*sx,84.83*sy, 285.07*sx,74.31*sy, 278.77*sx,68.6*sy)
            mask.bezierCurveTo(281.06*sx,34.27*sy, 296.25*sx,-13.96*sy, 239.61*sx,5.43*sy)
            mask.bezierCurveTo(217.44*sx,-5.95*sy, 196.13*sx,7.35*sy, 187.22*sx,0.83*sy)
            mask.bezierCurveTo(187.06*sx,2.3*sy, 188.43*sx,5.19*sy, 186.62*sx,3.13*sy)
            mask.bezierCurveTo(185.86*sx,2.63*sy, 186.66*sx,4.99*sy, 185.21*sx,3.46*sy)
            mask.bezierCurveTo(182.93*sx,3.58*sy, 173.2*sx,3.26*sy, 173.37*sx,4.47*sy)
            mask.bezierCurveTo(170.71*sx,4.83*sy, 168.53*sx,0.16*sy, 170.11*sx,3.96*sy)
            mask.bezierCurveTo(158.64*sx,-3.75*sy, 159.47*sx,6.63*sy, 147.73*sx,2.91*sy)
            mask.bezierCurveTo(142.73*sx,7.13*sy, 127.92*sx,2.0*sy, 122.04*sx,2.57*sy)
            mask.bezierCurveTo(116.69*sx,-0.7*sy, 111.58*sx,6.23*sy, 107.15*sx,1.57*sy)
            mask.bezierCurveTo(105.85*sx,1.26*sy, 105.29*sx,0.49*sy, 106.3*sx,2.21*sy)
            mask.bezierCurveTo(102.57*sx,-2.83*sy, 81.76*sx,10.12*sy, 80.3*sx,-0.02*sy)
            mask.bezierCurveTo(73.32*sx,4.3*sy, 64.35*sx,-0.21*sy, 54.98*sx,0.24*sy)
            mask.bezierCurveTo(43.65*sx,4.34*sy, 49.55*sx,2.18*sy, 36.76*sx,1.89*sy)
            mask.bezierCurveTo(30.07*sx,4.22*sy, 2.49*sx,-2.7*sy, 3.58*sx,8.45*sy)
            mask.bezierCurveTo(11.18*sx,30.48*sy, 9.46*sx,51.93*sy, 5.16*sx,74.29*sy)
            mask.bezierCurveTo(1.67*sx,82.92*sy, 9.9*sx,96.24*sy, 2.56*sx,103.71*sy)
            mask.bezierCurveTo(-0.12*sx,114.05*sy, 2.18*sx,122.54*sy, 2.7*sx,133.03*sy)
            mask.bezierCurveTo(-0.57*sx,139.88*sy, -0.84*sx,151.34*sy, 1.76*sx,158.37*sy)
            mask.bezierCurveTo(-3.57*sx,167.29*sy, 6.24*sx,171.76*sy, 2.79*sx,178.96*sy)
            mask.bezierCurveTo(2.97*sx,208.41*sy, 55.42*sx,196.62*sy, 75.73*sx,198.09*sy)
            mask.bezierCurveTo(139.97*sx,209.18*sy, 206.23*sx,218.03*sy, 270.27*sx,203.54*sy)
            mask.bezierCurveTo(286.33*sx,208.75*sy, 278.35*sx,164.73*sy, 277.06*sx,155.21*sy)
            mask.bezierCurveTo(277.8*sx,145.23*sy, 283.84*sx,138.23*sy, 285.13*sx,128.93*sy)
            mask.closePath()
            mask.endFill()
            mask.x = imgX
            mask.y = imgY
            this.addChild(mask)
            imgSprite.mask = mask
            this.addChild(imgSprite)
        }

        // Background sprite (randomised by ID) — on top of image
        if (assets.stickyNotes && assets.stickyNotes.length) {
            this.sprite = id % assets.stickyNotes.length
            const bgSprite = new PIXI.Sprite(assets.stickyNotes[this.sprite])
            bgSprite.width = POSTIT_WIDTH
            bgSprite.height = POSTIT_HEIGHT
            this.addChild(bgSprite)
        } else {
            // Fallback: flat colour
            const bg = new PIXI.Graphics()
            bg.beginFill(0xFFF9C4)
            bg.drawRect(0, 0, POSTIT_WIDTH, POSTIT_HEIGHT)
            bg.endFill()
            this.addChild(bg)
        }

        // Tape over top-left corner of image (on top of everything)
        if (hasImage && assets.tape) {
            const tape = new PIXI.Sprite(assets.tape)
            tape.anchor.set(0.5, 0.5)
            const imgSize = (POSTIT_WIDTH - PADDING * 2) * 1.4
            tape.x = imgCornerX + imgSize * 0.05
            tape.y = imgCornerY + imgSize * 0.035
            tape.rotation = (-25 * Math.PI) / 180
            tape.scale.set(0.65)
            this.addChild(tape)
        }

        let textY = PADDING + 16

        // Number
        if (number) {
            fill = this.sprite === 1 ? 0xf7e3ce : 0x65677E
            const numText = new PIXI.Text(`#${number}`, {
                fontFamily: HEADING_FONT,
                fontSize: 24,
                fill: fill,
            })
            if (hasImage) {
                // Top right when image is present
                numText.anchor.set(1, 0)
                numText.x = POSTIT_WIDTH - PADDING
                numText.y = PADDING + 8
            } else {
                numText.x = PADDING
                numText.y = textY
            }
            textY += 80
            this.addChild(numText)
        }

        // Scale font size by text length
        const len = text.length
        let fontSize, lineHeight
        if (len <= 20) {
            fontSize = 40
            lineHeight = 42
        } else if (len <= 50) {
            fontSize = 32
            lineHeight = 34
        } else if (len <= 75) {
            fontSize = 26
            lineHeight = 28
        } else {
            fontSize = 22
            lineHeight = 24
        }

        // Main text
        const mainText = new PIXI.Text(text.toUpperCase(), {
            fontFamily: MESSAGE_FONT,
            fontSize,
            fill: 0x000000,
            wordWrap: true,
            wordWrapWidth: POSTIT_WIDTH - PADDING * 2,
            lineHeight,
            align: 'center',
        })
        mainText.anchor.set(0.5, 0)
        mainText.x = POSTIT_WIDTH / 2
        mainText.y = textY
        this.addChild(mainText)

        // Author name
        if (author_name) {
            fill = this.sprite === 1 ? 0xf7e3ce : 0x65677E

            const authorText = new PIXI.Text(author_name, {
                fontFamily: HEADING_FONT,
                fontSize: 18,
                fill: fill,
            })
            authorText.anchor.set(0.5, 0)
            authorText.x = POSTIT_WIDTH / 2
            authorText.y = textY + mainText.height + 12
            this.addChild(authorText)
        }

        // Set pivot to center for rotation
        this.pivot.set(POSTIT_WIDTH / 2, POSTIT_HEIGHT / 2)
    }
}

export { getShadowTexture }
